// UserContext.jsx
import { useNavigate } from 'react-router-dom'
import { GET_USER_DATA } from '../apollo/queries/client'
import { getCurrentUser, signOut } from 'aws-amplify/auth'
import { gql, useApolloClient, useQuery } from '@apollo/client'
import { GET_MANUFACTURE } from '../apollo/queries/getManufacture'
import { createContext, useContext, useEffect, useState } from 'react'

const UserContext = createContext()

const UserProvider = ({ children }) => {
  const client = useApolloClient()
  const [user, setUser] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const [userId, setUserId] = useState(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [openModal, setOpenModal] = useState(false)
  const [errorModal, setErrorModal] = useState(false)
  const [closeModal, setCloseModal] = useState(false)
  const [AddEventModal, setAddEventModal] = useState(false)

  const [changeBtn, setChangeBtn] = useState(false)
  const [editModal, setEditModal] = useState(false)
  const [event, setEvent] = useState(false)
  const [cardId, setCardId] = useState('')
  const [items, setItems] = useState(null)
  const [defaultImage, setDefaultImage] = useState(false)

  const reset = async () => {
    localStorage.removeItem('isSignedIn')
    localStorage.removeItem('email')
    localStorage.removeItem('brand')
    localStorage.removeItem('selectedOrdersBrandRoute')
    setUser(null)
    setUserId(null)
    try {
      await client.clearStore()
    } catch (error) {
      console.error('Error clearing Apollo store:', error)
    }
    try {
      await signOut()
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  const {
    data,
    loading: apolloLoading,
    refetch,
    error: apolloError,
  } = useQuery(gql(GET_USER_DATA), {
    fetchPolicy: 'network-only',
    skip: !user,
  })

  useEffect(() => {
    if (apolloError) {
      console.error('Apollo Error:', apolloError)
      reset()
    }
  }, [apolloError])

  useEffect(() => {
    if (!apolloLoading && user && !data?.user) {
      reset()
    }
  }, [apolloLoading, user, data])

  const { data: manufactureData } = useQuery(GET_MANUFACTURE, {
    variables: { email: data?.user?.email },
    skip: !data?.user?.email,
  })

  const isBusinessInfo = data?.user?.type !== "Member" && data?.user?.manufacturerObject?.isBusinessInfo === true
  const isShopifyConnected = data?.user?.type !== "Member" && data?.user?.manufacturerObject?.isShopifyConnected === true
  const isStripeConnected = data?.user?.type !== "Member" && data?.user?.manufacturerObject?.isStripeConnected === true
  console.log({ isBusinessInfo, isShopifyConnected, isStripeConnected })

  const isSaddlefitAdmin = data?.user?.brand === 'saddlefitadmin' && data?.user?.type === 'Admin';
  const isCactusAdmin = data?.user?.brand === 'cactus' && (data?.user?.type === 'Admin' || data?.user?.type === 'Member');
  const isManufacturerAdmin = data?.user?.type === 'Admin' && !['cactus', 'saddlefitadmin'].includes(data?.user?.brand);
  const isTeamMember = data?.user?.type === 'Member' && !['saddlefitadmin'].includes(data?.user?.brand);
  // View-only account invited via File Browser "Manage access" — may only see
  // private folders shared with them.
  const isPrivateViewer = data?.user?.brand === 'saddlefitadmin' && data?.user?.type === 'Member';

  const stepperSteps = [
    {
      id: 'step1',
      title: 'Business Information',
      isCompleted: isBusinessInfo,
    },
    {
      id: 'step2',
      title: 'Shopify Store',
      isCompleted: isShopifyConnected,
    },
    {
      id: 'step3',
      title: 'Stripe Account',
      isCompleted: isStripeConnected,
    },
  ]

  const isSignedIn = Boolean(localStorage.getItem('isSignedIn'))
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: '',
    message: '',
  })

  const openMessageModal = (title, message) => {
    setModalState({
      isOpen: true,
      title: title,
      message: message,
    })
  }

  const closeMessageModal = () => {
    setModalState(prevState => ({
      ...prevState,
      isOpen: false,
    }))
  }
  const navigate = useNavigate()

  const fetchUser = async () => {
    try {
      const resp = await getCurrentUser()
      setUser(resp)
      setUserId(resp?.userId)
    } catch (err) {
      console.error(err)
      await reset()
    } finally {
      setAuthLoading(false)
    }
  }

  useEffect(() => {
    if (isSignedIn) {
      fetchUser()
    } else {
      setAuthLoading(false)
    }
  }, [])


  const onSignOutHandler = async () => {
    try {
      setAuthLoading(true)
      await reset();

      navigate('/', { replace: true })
    } catch (error) {
      console.error('Sign-out failed:', error)
    } finally {
      setAuthLoading(false)
    }
  }

  const isLoading =
    authLoading || (!user && !isSignedIn ? false : apolloLoading)

  return (
    <UserContext.Provider
      value={{
        user,
        userId,
        setUser,
        changeBtn,
        setChangeBtn,
        defaultImage,
        setDefaultImage,
        items,
        editModal,
        setItems,
        setEditModal,
        event,
        cardId,
        setCardId,
        fetchUser,
        setEvent,
        AddEventModal,
        setAddEventModal,
        closeModal,
        setCloseModal,
        openModal,
        setOpenModal,
        errorModal,
        setErrorModal,
        onSignOutHandler,
        modalState,
        setModalState,
        openMessageModal,
        closeMessageModal,
        reset,
        setAuthLoading,
        userData: data?.user,
        userLoading: isLoading,
        refetchUser: refetch,
        stepperSteps,
        isOpen,
        setIsOpen,
        isManufacturerAdmin,
        isTeamMember,
        isCactusAdmin,
        isSaddlefitAdmin,
        isPrivateViewer
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

const useUserContext = () => {
  const ctx = useContext(UserContext)
  if (!ctx) throw new Error('useUserContext must be used within UserProvider')
  return ctx
}

export { UserProvider, useUserContext }
